import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Clock, Flag, ArrowLeft, ArrowRight, Check } from "@phosphor-icons/react";
import ProctoringWatcher from "../../components/ProctoringWatcher";
import StudentProctorChat from "../../components/StudentProctorChat";
import AudioViolationWatcher from "../../components/AudioViolationWatcher";
import CodingPanel from "../../components/CodingPanel";
import RichText from "../../components/RichText";
import { MediaRenderer } from "../../components/MediaAttachments";

export default function TakeExam() {
  const { eid } = useParams();
  const [sp] = useSearchParams();
  const attemptId = sp.get("attempt");
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> {answer:[], marked_for_review}
  const [remaining, setRemaining] = useState(null); // seconds
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState("");

  // load
  useEffect(() => {
    if (!attemptId) return;
    (async () => {
      try {
        const [a, qs] = await Promise.all([
          api.get(`/attempts/${attemptId}`),
          api.get(`/attempts/${attemptId}/questions`),
        ]);
        setAttempt(a.data);
        setQuestions(qs.data);
        setAnswers(a.data.answers || {});
        const exp = new Date(a.data.expires_at);
        setRemaining(Math.max(0, Math.floor((exp.getTime() - Date.now()) / 1000)));
        setPaused(a.data.status === "paused");
        setPauseReason(a.data.pause_reason || "");
        try {
          const er = await api.get(`/exams/${eid}`);
          setExam(er.data);
        } catch { /* exam metadata is optional */ }
      } catch (e) {
        toast.error("Failed to load attempt");
        navigate("/app/exams");
      }
    })();
  }, [attemptId, eid, navigate]);

  // countdown
  useEffect(() => {
    if (remaining == null) return;
    if (paused) return; // freeze timer while paused
    if (remaining <= 0) {
      handleSubmit(true);
      return;
    }
    const t = setInterval(() => setRemaining((r) => (r != null ? r - 1 : null)), 1000);
    return () => clearInterval(t);
  }, [remaining, paused]);

  const current = questions[idx];

  const saveAnswer = useCallback(async (qid, answerArr, marked, testResult) => {
    const prev = answers[qid] || {};
    const next = { ...answers, [qid]: { answer: answerArr, marked_for_review: !!marked, test_result: testResult ?? prev.test_result } };
    setAnswers(next);
    try {
      await api.post(`/attempts/${attemptId}/answer`, {
        question_id: qid,
        answer: answerArr,
        marked_for_review: !!marked,
        test_result: testResult ?? prev.test_result,
      });
    } catch {
      // silent — will retry on next interaction
    }
  }, [answers, attemptId]);

  const handleSubmit = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/submit`);
      toast.success(auto ? "Time up — exam submitted" : "Exam submitted");
      navigate(`/app/results/${r.data.attempt_id}`, { replace: true });
    } catch (e) {
      toast.error("Submit failed");
      setSubmitting(false);
    }
  };

  const timeStr = useMemo(() => {
    if (remaining == null) return "--:--";
    const h = Math.floor(remaining / 3600);
    const m = Math.floor((remaining % 3600) / 60);
    const s = remaining % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  }, [remaining]);

  if (!current) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading exam…</div>;
  }

  const ans = answers[current.question_id] || { answer: [], marked_for_review: false };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar */}
      <header className="border-b border-zinc-200 h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 bg-white z-10 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Exam in progress</div>
          <div className="font-display font-bold text-sm sm:text-base truncate">Question {idx + 1} of {questions.length}</div>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock size={18} className={remaining != null && remaining < 60 ? "text-red-600" : "text-zinc-900"} />
            <div className={`font-mono-tab font-bold text-lg sm:text-xl ${remaining != null && remaining < 60 ? "text-red-600" : "text-zinc-900"}`} data-testid="exam-timer">
              {timeStr}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] min-h-0">
        {/* Question area */}
        <div className="overflow-y-auto p-4 sm:p-8 lg:p-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                {qTypeLabel(current.type)}
              </div>
              <span className="text-zinc-300">·</span>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                {current.marks} marks
              </div>
              {current.difficulty && (
                <>
                  <span className="text-zinc-300">·</span>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                    {current.difficulty}
                  </div>
                </>
              )}
            </div>
            <div className="font-display text-2xl sm:text-3xl font-bold leading-snug mb-4" data-testid="question-text">
              <RichText html={current.text} />
            </div>
            <MediaRenderer attachments={current.attachments} />
            <div className="mt-6" />

            <OptionsRenderer
              question={current}
              value={ans.answer}
              onChange={(a) => saveAnswer(current.question_id, a, ans.marked_for_review)}
              onTestResult={(tr) => saveAnswer(current.question_id, ans.answer, ans.marked_for_review, tr)}
            />

            {/* Mark for review — kept inline; nav buttons moved to bottom bar */}
            <div className="mt-10 pt-6 border-t border-zinc-200">
              <button
                onClick={() => saveAnswer(current.question_id, ans.answer, !ans.marked_for_review)}
                className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-medium ${
                  ans.marked_for_review
                    ? "bg-amber-500 text-white border-amber-600"
                    : "border-zinc-300 hover:bg-zinc-50"
                }`}
                data-testid="mark-review-btn"
              >
                <Flag size={14} weight={ans.marked_for_review ? "fill" : "regular"} />
                {ans.marked_for_review ? "Marked for review" : "Mark for review"}
              </button>
            </div>
          </div>
        </div>

        {/* Navigator + inline camera */}
        <aside className="border-t lg:border-t-0 lg:border-l border-zinc-200 bg-zinc-50 overflow-y-auto flex flex-col" data-testid="question-navigator">
          {/* Inline camera preview — attached inside the exam panel */}
          {exam?.proctoring_enabled !== false && !paused && (
            <div className="border-b border-zinc-200 bg-zinc-900" data-testid="exam-camera-panel">
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="text-[9px] uppercase tracking-[0.25em] text-white/70 font-semibold inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full pulse-dot" />
                  Live · You are being recorded
                </div>
              </div>
              <video
                id="exam-camera-video"
                muted
                autoPlay
                playsInline
                ref={(el) => { window.__examCameraTarget = el; }}
                className="w-full aspect-video object-cover bg-black"
              />
              {attemptId && exam?.proctoring_enabled !== false && !paused && (
                <div className="p-2 border-t border-zinc-200 flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-wider">
                  <span>Audio</span>
                  <AudioViolationWatcher attemptId={attemptId} />
                </div>
              )}
            </div>
          )}

          <div className="p-6 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 font-semibold">Question Navigator</div>
            <div className="grid grid-cols-5 gap-2 mb-6">
              {questions.map((q, i) => {
                const a = answers[q.question_id];
                const answered = a && a.answer && a.answer.length > 0;
                const marked = a && a.marked_for_review;
                let cls = "bg-white text-zinc-900 border-zinc-300 hover:bg-zinc-100";
                if (marked) cls = "bg-amber-500 text-white border-amber-600";
                else if (answered) cls = "bg-green-600 text-white border-green-700";
                if (i === idx) cls += " ring-2 ring-zinc-900 ring-offset-2";
                return (
                  <button
                    key={q.question_id}
                    onClick={() => setIdx(i)}
                    className={`w-10 h-10 flex items-center justify-center text-sm font-medium border transition-colors ${cls}`}
                    data-testid={`navigator-q-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2 text-xs">
              <Legend color="bg-green-600" label="Answered" />
              <Legend color="bg-amber-500" label="Marked for review" />
              <Legend color="bg-white border border-zinc-300" label="Unanswered" />
              <Legend color="ring-2 ring-zinc-900 bg-white" label="Current" />
            </div>
          </div>
        </aside>
      </div>

      {/* Sticky bottom action bar. On desktop the right padding reserves space for the
          floating chat toggle (56px round button at right-6, bottom-6). On mobile the
          chat toggle is moved up (see StudentProctorChat) so the buttons can span the
          full width and wrap when needed. */}
      <div className="border-t border-zinc-200 bg-white px-4 sm:px-6 sm:pr-24 py-3 flex flex-wrap items-center justify-between gap-2 sm:gap-3 sticky bottom-0 z-20" data-testid="exam-bottom-bar">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className="inline-flex items-center gap-2 border border-zinc-300 px-3 sm:px-5 py-2.5 text-xs sm:text-sm font-medium hover:bg-zinc-50 disabled:opacity-40"
          data-testid="prev-question-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Previous
        </button>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto flex-wrap justify-end">
          {idx < questions.length - 1 && (
            <button
              onClick={() => {
                // Ensure the current answer is persisted before advancing. saveAnswer is
                // already called on every input change so this is defensive & idempotent.
                const cur = questions[idx];
                if (cur) {
                  const a = answers[cur.question_id] || { answer: [], marked_for_review: false };
                  saveAnswer(cur.question_id, a.answer, a.marked_for_review, a.test_result);
                }
                setIdx((i) => Math.min(questions.length - 1, i + 1));
              }}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-bold hover:bg-blue-700 border-2 border-blue-600 shadow-lg"
              data-testid="save-next-btn"
            >
              Save &amp; Next <ArrowRight size={14} weight="bold" />
            </button>
          )}
          <button
            onClick={() => setConfirm(true)}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-bold hover:bg-red-700 border-2 border-red-600 shadow-lg"
            data-testid="submit-exam-btn"
          >
            Submit exam
          </button>
        </div>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit exam?</DialogTitle>
            <DialogDescription>
              You answered {Object.values(answers).filter((a) => a.answer.length > 0).length} of {questions.length} questions.
              You won&apos;t be able to make changes after submitting.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setConfirm(false)}
              className="border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
              data-testid="confirm-cancel-btn"
            >Cancel</button>
            <button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="bg-zinc-900 text-white px-4 py-2 text-sm hover:bg-zinc-800 disabled:opacity-50"
              data-testid="confirm-submit-btn"
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProctoringWatcher attemptId={attemptId} enabled={!!attemptId && exam?.proctoring_enabled !== false && !paused} hidden={true} />
      <StudentProctorChat
        attemptId={attemptId}
        onAutoSubmit={() => {
          // Server has already flipped status. Give the student a moment to read the
          // warning toast + drawer message, then navigate to results.
          setTimeout(() => navigate(`/app/results/${attemptId}`, { replace: true }), 3000);
        }}
        onStatusChange={(st, extra) => {
          if (st === "paused") {
            setPaused(true);
            setPauseReason(extra?.pause_reason || "");
            api.get(`/attempts/${attemptId}`).then((r) => {
              setAttempt(r.data);
              setPauseReason(r.data.pause_reason || "");
            }).catch(() => {});
          } else if (st === "in_progress") {
            setPaused(false);
            // Refresh remaining time from server (expires_at was extended on resume)
            api.get(`/attempts/${attemptId}`).then((r) => {
              setAttempt(r.data);
              const exp = new Date(r.data.expires_at);
              setRemaining(Math.max(0, Math.floor((exp.getTime() - Date.now()) / 1000)));
              toast.success("Exam resumed by proctor");
            }).catch(() => {});
          }
        }}
      />

      {/* Paused overlay — freezes exam until proctor resumes */}
      {paused && (
        <div className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-6" data-testid="student-paused-overlay">
          <div className="bg-white max-w-lg w-full p-8 border-4 border-amber-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-500 flex items-center justify-center text-white">
                <span className="text-2xl">⏸</span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-semibold">Exam paused</div>
                <div className="font-display text-2xl font-bold">Waiting for proctor…</div>
              </div>
            </div>
            <div className="text-sm text-zinc-700 border-l-2 border-amber-500 pl-3 my-4">
              {pauseReason || "Your exam has been paused by the proctor. Please wait — the timer is frozen and will resume when the proctor allows you to continue."}
            </div>
            <ul className="text-xs text-zinc-600 space-y-1.5 mt-4">
              <li>· The remaining time will be preserved.</li>
              <li>· Answers you&apos;ve saved are safe.</li>
              <li>· Do not refresh or close this tab.</li>
              <li>· You cannot submit or navigate questions while paused.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-5 h-5 inline-block ${color}`} />
      <span className="text-zinc-600">{label}</span>
    </div>
  );
}

function qTypeLabel(t) {
  return ({
    mcq: "Multiple Choice",
    msq: "Multiple Select",
    true_false: "True / False",
    short_answer: "Short Answer",
  })[t] || t;
}

function OptionsRenderer({ question, value, onChange, onTestResult }) {
  if (question.type === "coding") {
    const codeStr = (value && value[0]) || question.starter_code || "";
    const v = { language: question.language || "python", code: codeStr };
    return (
      <CodingPanel
        question={question}
        value={v}
        onChange={(payload) => onChange([payload.code])}
        onTestResult={onTestResult}
        attemptMode
      />
    );
  }

  if (question.type === "short_answer") {
    return (
      <textarea
        rows={5}
        value={value[0] || ""}
        onChange={(e) => onChange([e.target.value])}
        className="w-full border border-zinc-300 p-4 text-base focus:border-zinc-900 focus:outline-none"
        placeholder="Type your answer here…"
        data-testid="answer-text-input"
      />
    );
  }

  const multi = question.type === "msq";
  const options = question.options || (question.type === "true_false" ? ["True", "False"] : []);
  const optionAttachments = question.option_attachments || {};

  const toggle = (idx) => {
    const sIdx = String(idx);
    if (multi) {
      onChange(value.includes(sIdx) ? value.filter((v) => v !== sIdx) : [...value, sIdx]);
    } else {
      onChange([sIdx]);
    }
  };

  return (
    <div className="space-y-3">
      {options.map((opt, i) => {
        const sel = value.includes(String(i));
        const optAtt = optionAttachments[String(i)] || [];
        return (
          <button
            key={i}
            onClick={() => toggle(i)}
            className={`w-full text-left border px-5 py-4 flex items-start gap-4 transition-colors ${
              sel ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"
            }`}
            data-testid={`option-${i}`}
          >
            <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border ${sel ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-500"} ${multi ? "" : "rounded-full"}`}>
              {sel ? <Check size={14} weight="bold" /> : <span className="text-xs">{String.fromCharCode(65 + i)}</span>}
            </span>
            <span className="flex-1">
              <span className="text-base block">{opt}</span>
              {optAtt.length > 0 && <span className="block mt-2"><MediaRenderer attachments={optAtt} inline /></span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
